import { useEffect, useRef, type ReactNode } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { OrganizationTheme, Enduser } from "@tellescope/types-models";
import {
  useEnduserIsAuthenticated,
  useEnduserIsLoading,
  useEnduserTokenError,
  useEnduserInitFromToken,
  useEnduserOtpMethods,
  useEnduserOtpMode,
  useEnduserCurrentEnduser,
  useEnduserLogout,
} from "../store/hooks";
import { useOrganization } from "../../shared/store/organizationStore";
import { useIsLandingPageDisabled, usePortalSchemaStore } from "../../shared/store/portalSchemaStore";
import { LoadingScreen } from "../../shared/components/LoadingScreen";
import { ErrorPage } from "../pages/ErrorPage";
import { OTPRecoveryPage } from "../pages/OTPRecoveryPage";
import { LandingPage } from "../pages/LandingPage";
import { TermsAcceptancePage } from "../pages/TermsAcceptancePage";
import { EnduserQueryProvider } from "./EnduserQueryProvider";
import { EnduserHomePage } from "../pages/HomePage";
import { OrganizationThemeProvider } from "../../shared/components/OrganizationThemeProvider";
import { VideoCallingProvider } from "../providers/VideoCallingProvider";
import { usePartnerRedirect } from "../hooks/usePartnerRedirect";

/**
 * Check if the enduser needs to accept Terms of Service / Privacy Policy.
 * Returns true if:
 * - Organization has custom policies configured (TOS, Privacy, or custom)
 * - AND enduser hasn't accepted the current version
 */
const requiresTermsAcceptance = (organization: OrganizationTheme | null, enduser: Enduser | null): boolean => {
  if (!organization || !enduser) return false;

  // Check if any custom policies are configured
  const hasPolicies = !!(
    organization.customTermsOfService ||
    organization.customPrivacyPolicy ||
    (organization.customPolicies && organization.customPolicies.length > 0)
  );

  if (!hasPolicies) return false;

  // Check if user has signed current version
  const currentVersion = organization.customPoliciesVersion || "v1";
  return enduser.termsVersion !== currentVersion;
};

interface EnduserRouteContentProps {
  children?: ReactNode;
}

/**
 * Inner component that handles the actual routing logic.
 * Separated to allow wrapping with OrganizationThemeProvider.
 */
const EnduserRouteContent = ({ children }: EnduserRouteContentProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Auth state hooks
  const isAuthenticated = useEnduserIsAuthenticated();
  const isLoading = useEnduserIsLoading();
  const tokenError = useEnduserTokenError();
  const initFromToken = useEnduserInitFromToken();
  const otpMethods = useEnduserOtpMethods();
  const otpMode = useEnduserOtpMode();
  const currentEnduser = useEnduserCurrentEnduser();
  const logout = useEnduserLogout();
  const organization = useOrganization();
  const isLandingPageDisabled = useIsLandingPageDisabled();
  const fetchSchema = usePortalSchemaStore((state) => state.fetchSchema);
  const partnerRedirectStatus = usePartnerRedirect(isAuthenticated);

  // Fetch portal schema early so settings (e.g. disableSMSForMFA) are available for the OTP page
  useEffect(() => {
    if (organization) {
      fetchSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSchema]);

  // Track if we've already attempted authentication to prevent infinite loops
  const hasAttemptedAuth = useRef(false);
  const previousToken = useRef<string | null>(null);

  // Reset the auth attempt flag if token changes
  useEffect(() => {
    if (token !== previousToken.current) {
      hasAttemptedAuth.current = false;
      previousToken.current = token;
    }
  }, [token]);

  useEffect(() => {
    // If we have a token in URL, ALWAYS process it (even if already authenticated)
    // This ensures new tokens take precedence over cached sessions for security
    if (token && !isLoading && !tokenError && !hasAttemptedAuth.current) {
      hasAttemptedAuth.current = true;

      initFromToken(token).then((success) => {
        if (success) {
          // Remove token from URL after successful auth (one-time use)
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("token");
          setSearchParams(newParams, { replace: true });
        }
        // If failed, error is already set in store by initFromToken
      });
    }
  }, [token, isLoading, tokenError, initFromToken, searchParams, setSearchParams]);

  // Show loading while authenticating
  if (isLoading) {
    return <LoadingScreen message="Authenticating..." />;
  }

  // If OTP verification is required (recovery or MFA), show OTP page
  // otpMode indicates the context: "recovery" for expired token, "mfa" for post-password
  if (otpMode && otpMethods && otpMethods.length > 0) {
    return <OTPRecoveryPage />;
  }

  // Show error if token validation failed (and no OTP recovery available)
  if (tokenError) {
    return <ErrorPage error={tokenError} />;
  }

  // Still processing token (waiting for useEffect to run)
  if (token && !isAuthenticated) {
    return <LoadingScreen message="Validating access..." />;
  }

  // If authenticated but locked from portal, show access disabled message
  if (isAuthenticated && currentEnduser?.lockedFromPortal) {
    return (
      <ErrorPage error={t("Your portal access has been disabled.")} actionLabel={t("Log out")} onAction={logout} />
    );
  }

  // If authenticated but requires TOS acceptance, show terms page
  if (isAuthenticated && requiresTermsAcceptance(organization, currentEnduser)) {
    return <TermsAcceptancePage />;
  }

  // If authenticated and a partner redirect was captured pre-auth, show the loading
  // screen while usePartnerRedirect mints a JWT and navigates back to the partner.
  // On any failure path the hook flips to "complete" (after toast), and we fall
  // through to render the home page.
  if (isAuthenticated && partnerRedirectStatus === "redirecting") {
    return <LoadingScreen message={t("Redirecting...")} />;
  }

  // If authenticated, render children (for legacy routes) or home page
  if (isAuthenticated) {
    return (
      <EnduserQueryProvider>
        <VideoCallingProvider>{children ?? <EnduserHomePage />}</VideoCallingProvider>
      </EnduserQueryProvider>
    );
  }

  // Not authenticated and no token - check landing page setting
  if (isLandingPageDisabled) {
    return <Navigate to="/login" replace />;
  }

  return <LandingPage />;
};

interface EnduserRouteHandlerProps {
  children?: ReactNode;
}

/**
 * EnduserRouteHandler handles the routing logic for the root and portal pages.
 *
 * Flow:
 * 1. If has token in URL → process token (authenticate)
 * 2. If OTP recovery available → show OTP recovery page
 * 3. If token error → show error page
 * 4. If authenticated AND requires TOS → show TermsAcceptancePage
 * 5. If authenticated → render children or EnduserHomePage (wrapped in EnduserQueryProvider)
 * 6. If not authenticated → render LandingPage (or redirect to /login if disabled)
 *
 * Wrapped in OrganizationThemeProvider to apply organization brand colors.
 *
 * @param children - Optional children to render instead of EnduserHomePage when authenticated.
 *                   Used for legacy route fallback pages.
 */
export const EnduserRouteHandler = ({ children }: EnduserRouteHandlerProps) => {
  return (
    <OrganizationThemeProvider>
      <EnduserRouteContent children={children} />
    </OrganizationThemeProvider>
  );
};
